import { Translation } from '@ps/web-shared-components';
import { addDays, addHours, differenceInDays } from 'date-fns';
import React from 'react';

import InfoMessage from '@swf/components/InfoMessage/InfoMessage';
import { StatusCode } from '@swf/core/enums/statusCode';

import { ExclusionTime } from '../../../shared/definitions';
import AccountDate from '../../AccountDate/AccountDate';
import ExclusionRevocationForm from '../../ExclusionRevocationForm/ExclusionRevocationForm';
import { ExclusionType } from '../../definitions';

import {
  TXTWCL_This_means_you_wonXt_be_able_t_ELL_14,
  TXTWCL_This_means_you_wonXt_be_able_t_ELL_5,
  TXTWCL_WeXll_automatically_reopen_you_ELL,
  TXTWCL_WeXll_automatically_reopen_you_ELL_1,
  TXTWCL_We_canXt_process_your_request__ELL_1,
  TXTWCL_YouXre_taking_a_break,
  TXTWCL_You_can_request_to_end_your_ex_ELL,
  TXTWCL_You_have_opted_to_end_your_sel_ELL,
  TXTWCL_You_will_be_logged_outX_and_yo_ELL
} from './ExclusionResponsePT.messages.json';

interface Props {
  error?: boolean;
  exclusionType?:
    | ExclusionType.Break
    | ExclusionType.SelfExclusion
    | ExclusionType.PermanentSelfExclusion;
  statusCode?: StatusCode;
  timeUntil?: number | string;
  daysRequested?: number;
  selfExcludedInfoFlags?: number;
}

const MIN_REQUESTED_REVOCATION_DAYS = 90;
const REVOCATION_IN_PROGRESS_CODE = 1;

const ExclusionResponsePT: React.FC<Props> = ({
  error,
  statusCode,
  exclusionType,
  timeUntil,
  daysRequested,
  selfExcludedInfoFlags
}) => {
  const getEndDate = () => {
    const currentDate = new Date();
    if (timeUntil) {
      return new Date(timeUntil);
    } else if (daysRequested === ExclusionTime.HalfDay) {
      return addHours(currentDate, 12);
    } else {
      return addDays(currentDate, daysRequested || 0);
    }
  };

  const endDate = getEndDate();

  const userCanRevoke = daysRequested
    ? daysRequested > MIN_REQUESTED_REVOCATION_DAYS && differenceInDays(endDate, new Date()) > 30
    : false;
  const userHasRevoked = selfExcludedInfoFlags === REVOCATION_IN_PROGRESS_CODE;
  const hasAlreadySelfExcluded = !statusCode && exclusionType === ExclusionType.SelfExclusion;
  const selfExclusionSuccessful = statusCode === StatusCode.USER_SELF_EXCLUDED;

  const formattedEndDate = (
    <b>
      <AccountDate date={endDate} />
    </b>
  );

  switch (true) {
    case error:
      return (
        <InfoMessage type="error">
          <Translation {...TXTWCL_We_canXt_process_your_request__ELL_1} />
        </InfoMessage>
      );

    case selfExclusionSuccessful && exclusionType === ExclusionType.Break:
      return (
        <InfoMessage type="confirmation" messageTitle={TXTWCL_YouXre_taking_a_break}>
          <Translation
            {...TXTWCL_WeXll_automatically_reopen_you_ELL_1}
            values={{
              0: formattedEndDate
            }}
          />
        </InfoMessage>
      );

    case selfExclusionSuccessful && exclusionType === ExclusionType.SelfExclusion:
      return (
        <InfoMessage type="confirmation" messageTitle={TXTWCL_YouXre_taking_a_break}>
          <Translation
            {...TXTWCL_WeXll_automatically_reopen_you_ELL_1}
            values={{
              0: formattedEndDate
            }}
          />{' '}
          <Translation {...TXTWCL_You_can_request_to_end_your_ex_ELL} />
        </InfoMessage>
      );

    case hasAlreadySelfExcluded && userCanRevoke:
      return <ExclusionRevocationForm formattedEndDate={formattedEndDate} />;

    case hasAlreadySelfExcluded && userHasRevoked:
      return (
        <Translation
          tagName="p"
          {...TXTWCL_You_have_opted_to_end_your_sel_ELL}
          values={{ 0: formattedEndDate }}
        />
      );

    case hasAlreadySelfExcluded:
      return (
        <Translation
          {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_5}
          values={{ 0: formattedEndDate }}
        />
      );

    case exclusionType === ExclusionType.PermanentSelfExclusion:
      return (
        <InfoMessage type="confirmation" messageTitle={TXTWCL_YouXre_taking_a_break}>
          <Translation {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_14} />{' '}
          <Translation {...TXTWCL_You_will_be_logged_outX_and_yo_ELL} />
        </InfoMessage>
      );

    default:
      return (
        <>
          <Translation
            {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_5}
            values={{ 0: formattedEndDate }}
          />{' '}
          <Translation {...TXTWCL_WeXll_automatically_reopen_you_ELL} />
        </>
      );
  }
};

export default ExclusionResponsePT;
